import { useState } from "react";
import { Download, Mail, CalendarDays, Smartphone, MapPin, Copy, Play } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, FadeIn, Reveal } from "@/components/Motion";

const RELEASE = `FOR IMMEDIATE RELEASE

VeloPaths Now Available on iOS and Android: Connecting Cyclists, Local Businesses, and Cycling Communities

NEWPORT, R.I. — VeloPaths, a new mobile platform for cyclists, is available now on iOS and Android. The app pairs curated bike paths with approved local stops — breweries, restaurants, cafés, ice cream shops, attractions, and rider essentials — replacing the clutter of generic map apps with a map built for life on two wheels.

Unlike generic mapping tools, every stop on VeloPaths is vetted by people who ride. Custom detours guide cyclists to local favorites and back to the path without losing momentum. Local businesses reach riders through a $99/year Founding Advertiser listing — pin placement in the Rider app and seasonal promotions via the VeloPaths Advertiser Portal — while partners including bike shops, cycling clubs, and local experts join a partner program with a $99 one-time setup that includes their first zone, a branded in-app profile, and a 50/50 share of advertising revenue.

"The best part of any ride is the stop you didn't plan," said the VeloPaths team. "We're building the map that finds it for you — and helps the local businesses along the route thrive."

VeloPaths is available now on the App Store and Google Play. Riders can get the app at velopaths.com. Businesses and partners can get started at app.velopaths.com.

Media contact: info@velopaths.com · (401) 646-0066
Velopaths, LLC · 270 Bellevue Avenue, PMB #1097, Newport, RI 02840, United States
Press kit: velopaths.com/press

###`;

const FACTS = [
  { icon: CalendarDays, label: "Availability", value: "iOS + Android — live now" },
  { icon: Smartphone, label: "Platforms", value: "iOS + Android" },
  { icon: MapPin, label: "Headquarters", value: "Newport, Rhode Island" },
  { icon: Mail, label: "Media contact", value: "info@velopaths.com" },
];

const VIDEOS = [
  {
    src: "/videos/founding-advertiser-web.mp4",
    poster: "/videos/founding-advertiser-poster.jpg",
    filename: "velopaths-founding-advertiser-promo.mp4",
    alt: "Cyclist riding a paved riverside path at golden hour",
    caption: "Founding Advertiser promo · 2 min",
    testid: "press-video-advertiser",
  },
  {
    src: "/videos/velopaths-partners-web.mp4",
    poster: "/videos/velopaths-partners-poster.jpg",
    filename: "velopaths-partner-program.mp4",
    alt: "VeloPaths partner presenter inside a bike rental shop",
    caption: "Partner Program explainer · 1.5 min",
    testid: "press-video-partner",
  },
  {
    src: "/videos/about-story-web.mp4",
    poster: "/videos/about-story-poster.jpg",
    filename: "velopaths-story-film.mp4",
    alt: "VeloPaths story film — presenter in a bike shop introducing the platform",
    caption: "The VeloPaths Story · 7 min",
    testid: "press-video-story",
  },
];

function PressVideo({ v }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div
      data-testid={v.testid}
      className="overflow-hidden rounded-2xl border border-[#154234]/10 bg-white"
    >
      {playing ? (
        <video
          src={v.src}
          poster={v.poster}
          controls
          autoPlay
          playsInline
          preload="auto"
          ref={(el) => {
            if (el) el.play().catch(() => {});
          }}
          data-testid={`${v.testid}-player`}
          className="aspect-video w-full bg-black"
        />
      ) : (
        <button
          onClick={() => setPlaying(true)}
          data-testid={`${v.testid}-play-button`}
          aria-label={`Play video: ${v.caption}`}
          className="group relative block w-full cursor-pointer"
        >
          <img
            src={v.poster}
            alt={v.alt}
            loading="lazy"
            decoding="async"
            className="aspect-video w-full object-cover"
          />
          <span className="absolute inset-0 bg-[#0B2B22]/30 transition-colors duration-300 group-hover:bg-[#0B2B22]/20" />
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid size-16 place-items-center rounded-full bg-[#E05A47] text-white shadow-2xl transition-transform duration-300 group-hover:scale-110">
              <Play size={24} className="ml-1" fill="currentColor" />
            </span>
          </span>
        </button>
      )}
      <div className="flex items-center justify-between gap-3 p-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#52635B]">
          {v.caption}
        </p>
        <a
          href={v.src}
          download={v.filename}
          data-testid={`btn-download-${v.testid}`}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#0B2B22] px-4 py-2.5 text-xs font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
        >
          <Download size={13} /> Download
        </a>
      </div>
    </div>
  );
}

const COLORS = [
  { name: "Pine", hex: "#0B2B22" },
  { name: "Emerald", hex: "#154234" },
  { name: "Sage", hex: "#2D6A4F" },
  { name: "Terracotta", hex: "#E05A47" },
  { name: "Azure", hex: "#38A3A5" },
  { name: "Trail Beige", hex: "#F4F7F4" },
];

const PHOTOS = [
  {
    src: "/images/home-hero-coastal.jpg",
    alt: "Riders on a paved coastal bike path",
    caption: "Coastal path · homepage hero",
    testid: "press-photo-coastal",
  },
  {
    src: "/images/bike-path-riders.jpg",
    alt: "Two riders on a tree-lined paved greenway",
    caption: "Greenway riders · spring",
    testid: "press-photo-greenway",
  },
];

export default function Press() {
  return (
    <main data-testid="page-press">
      <section className="relative overflow-hidden bg-[#0B2B22] pt-40 pb-20 sm:pb-24">
        <div className="grain absolute inset-0" />
        <div className="absolute -right-24 -top-24 size-72 rounded-full bg-[#2D6A4F]/40 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <FadeIn delay={0.05}>
            <p className="font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#7FB998]">
              Press & media
            </p>
          </FadeIn>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#F4F7F4]">
            <MaskedLines lines={["The VeloPaths", "Press Kit."]} />
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#F4F7F4]/75">
              Everything you need to cover the launch — brand assets, photography, key facts, and
              a direct line to the team.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 sm:py-24 lg:px-16">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FACTS.map((f, i) => (
            <Reveal key={f.label} delay={i * 0.08}>
              <div
                data-testid={`press-fact-${f.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl border border-[#154234]/10 bg-white p-6"
              >
                <span className="grid size-10 place-items-center rounded-xl bg-[#0B2B22] text-[#7FB998]">
                  <f.icon size={17} />
                </span>
                <p className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#52635B]">
                  {f.label}
                </p>
                <p className="mt-1 font-display text-lg font-bold tracking-tight text-[#0B2B22]">
                  {f.value}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <h2 className="mt-20 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2B22]">
            Brand logo
          </h2>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-[#52635B]">
            Use the light version on dark backgrounds and vice versa. Please don't stretch,
            recolor, or add effects.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-[#154234]/10 bg-white p-8">
              <div className="flex h-24 items-center justify-center">
                <img src="/velopaths-logo.png" alt="VeloPaths logo on light" className="h-10 w-auto" />
              </div>
              <a
                href="/velopaths-logo.png"
                download="velopaths-logo.png"
                data-testid="btn-download-logo-light"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B2B22] px-6 py-3 text-sm font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
              >
                <Download size={15} /> Download PNG
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="rounded-2xl bg-[#0B2B22] p-8">
              <div className="flex h-24 items-center justify-center">
                <span className="rounded-xl bg-white px-4 py-2.5">
                  <img src="/velopaths-logo.png" alt="VeloPaths logo on dark" className="h-8 w-auto" />
                </span>
              </div>
              <a
                href="/velopaths-logo.png"
                download="velopaths-logo.png"
                data-testid="btn-download-logo-dark"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E05A47] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#c94a39]"
              >
                <Download size={15} /> Download PNG
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <h2 className="mt-20 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2B22]">
            App download assets
          </h2>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-[#52635B]">
            Print-ready QR codes linking directly to the VeloPaths listings — ideal for
            articles, flyers, and broadcast overlays — plus the official store badges for
            digital coverage.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-[#154234]/10 bg-white p-8">
              <div className="flex h-36 items-center justify-center">
                <img
                  src="/images/app-store-qr.png"
                  alt="QR code linking to VeloPaths on the App Store"
                  className="h-32 w-32"
                />
              </div>
              <p className="mt-4 text-center font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#52635B]">
                iOS — App Store
              </p>
              <a
                href="/images/app-store-qr.png"
                download="velopaths-app-store-qr.png"
                data-testid="btn-download-qr-ios"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B2B22] px-6 py-3 text-sm font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
              >
                <Download size={15} /> Download PNG
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="rounded-2xl border border-[#154234]/10 bg-white p-8">
              <div className="flex h-36 items-center justify-center">
                <img
                  src="/images/play-store-qr.png"
                  alt="QR code linking to VeloPaths on Google Play"
                  className="h-32 w-32"
                />
              </div>
              <p className="mt-4 text-center font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#52635B]">
                Android — Google Play
              </p>
              <a
                href="/images/play-store-qr.png"
                download="velopaths-google-play-qr.png"
                data-testid="btn-download-qr-android"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B2B22] px-6 py-3 text-sm font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
              >
                <Download size={15} /> Download PNG
              </a>
            </div>
          </Reveal>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-[#154234]/10 bg-white p-8">
              <div className="flex h-24 items-center justify-center">
                <img
                  src="/images/app-store-badge.svg"
                  alt="Official Download on the App Store badge"
                  className="h-12 w-auto"
                />
              </div>
              <p className="mt-4 text-center font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#52635B]">
                App Store badge · SVG
              </p>
              <a
                href="/images/app-store-badge.svg"
                download="velopaths-app-store-badge.svg"
                data-testid="btn-download-badge-ios"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B2B22] px-6 py-3 text-sm font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
              >
                <Download size={15} /> Download SVG
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="rounded-2xl border border-[#154234]/10 bg-white p-8">
              <div className="flex h-24 items-center justify-center">
                <img
                  src="/images/google-play-badge.png"
                  alt="Official Get it on Google Play badge"
                  className="h-12 w-auto"
                />
              </div>
              <p className="mt-4 text-center font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#52635B]">
                Google Play badge · PNG
              </p>
              <a
                href="/images/google-play-badge.png"
                download="velopaths-google-play-badge.png"
                data-testid="btn-download-badge-android"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B2B22] px-6 py-3 text-sm font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
              >
                <Download size={15} /> Download PNG
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <h2 className="mt-20 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2B22]">
            Photography
          </h2>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-[#52635B]">
            Approved launch photography. Credit "VeloPaths" when publishing.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {PHOTOS.map((p, i) => (
            <Reveal key={p.src} delay={0.15 + i * 0.08}>
              <div
                data-testid={p.testid}
                className="overflow-hidden rounded-2xl border border-[#154234]/10 bg-white"
              >
                <img src={p.src} alt={p.alt} loading="lazy" decoding="async" className="h-64 w-full object-cover" />
                <div className="flex items-center justify-between gap-3 p-5">
                  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#52635B]">
                    {p.caption}
                  </p>
                  <a
                    href={p.src}
                    download
                    data-testid={`btn-download-${p.testid}`}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#0B2B22] px-4 py-2.5 text-xs font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
                  >
                    <Download size={13} /> Download
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <h2 className="mt-20 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2B22]">
            Video
          </h2>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-[#52635B]">
            Launch videos for broadcast and embed — play them here or download the MP4 files.
            Credit "VeloPaths" when publishing.
          </p>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {VIDEOS.map((v, i) => (
            <Reveal key={v.src} delay={0.15 + i * 0.08}>
              <PressVideo v={v} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <h2 className="mt-20 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2B22]">
            Brand colors
          </h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {COLORS.map((c, i) => (
            <Reveal key={c.hex} delay={0.1 + i * 0.05}>
              <div data-testid={`press-color-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div
                  className="h-20 rounded-2xl border border-[#154234]/10"
                  style={{ backgroundColor: c.hex }}
                />
                <p className="mt-2.5 text-sm font-bold text-[#0B2B22]">{c.name}</p>
                <p className="font-mono text-xs text-[#52635B]">{c.hex}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <h2 className="mt-20 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2B22]">
            Launch press release
          </h2>
          <p className="mt-3 max-w-xl text-sm sm:text-base text-[#52635B]">
            Ready to send — copy it, paste it into an email, and attach anything you need from
            this page.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div
            data-testid="press-release-block"
            className="mt-8 rounded-2xl border border-[#154234]/10 bg-white p-8 sm:p-10"
          >
            <p className="whitespace-pre-wrap font-serif text-sm sm:text-base leading-relaxed text-[#1A2521]">
              {RELEASE}
            </p>
            <button
              data-testid="btn-copy-release"
              onClick={() => {
                navigator.clipboard.writeText(RELEASE);
                toast.success("Press release copied to clipboard");
              }}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E05A47] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#c94a39]"
            >
              <Copy size={15} /> Copy press release
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-20 rounded-[2rem] bg-[#0B2B22] p-8 sm:p-12">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#7FB998]">
              Boilerplate
            </p>
            <p className="mt-5 max-w-3xl text-sm sm:text-base leading-relaxed text-[#F4F7F4]/80">
              VeloPaths connects cyclists, local businesses, and cycling communities. The app maps
              beautiful bike paths and layers them with approved local stops — breweries, cafés,
              ice cream shops, attractions, and essentials — curated with local partners
              instead of scraped from generic maps. VeloPaths is available now on iOS and Android,
              with an advertiser portal for local businesses and
              a partner program that shares revenue with local partners.
            </p>
            <a
              href="mailto:info@velopaths.com"
              data-testid="btn-press-contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E05A47] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#c94a39]"
            >
              <Mail size={15} /> Media inquiries — info@velopaths.com
            </a>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
